import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Phone, CreditCard, FileText } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => (
  <section
    className="relative min-h-[92vh] flex items-center overflow-hidden bg-gradient-to-br from-[#0A1B3D] via-[#12347C] to-[#1D4ED8]"
    data-testid="home-hero"
  >
    <div className="absolute inset-0 hero-grid-lines-light" aria-hidden="true" />
    <div className="absolute -top-48 -right-48 w-[640px] h-[640px] rounded-full bg-ember/25 blur-[160px]" aria-hidden="true" />
    <div className="absolute -bottom-40 left-1/4 w-[480px] h-[480px] rounded-full bg-white/10 blur-[140px]" aria-hidden="true" />

    <div className="relative mx-auto max-w-7xl px-6 lg:px-10 w-full pt-32 pb-24 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
      <div className="max-w-3xl">
        <h1 className="font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.04]">
          <MaskedLine delay={0.15}>The support you need.</MaskedLine>
          <MaskedLine delay={0.27}>
            The service <span className="text-ember">you deserve.</span>
          </MaskedLine>
        </h1>
        <motion.p
          className="mt-8 text-base md:text-lg text-blue-100/80 max-w-xl leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.65 }}
          data-testid="hero-subcopy"
        >
          Concorde Auto Acceptance services your auto loan with clarity and
          care — simple payment options, real people on the phone, and
          account answers when you need them.
        </motion.p>
        <motion.div
          className="mt-12 flex flex-wrap items-center gap-5"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <a
            href="https://my.blytzpay.com/#/merchant/7acdb786-1f15-4eca-aff5-f4b1fea02516/login"
            data-testid="hero-payment-button"
            className="inline-flex items-center gap-2 rounded-full bg-ember px-8 py-4 text-base font-bold text-white transition-all duration-300 hover:bg-tangerine hover:scale-105 hover:shadow-[0_0_36px_rgba(255,87,34,0.5)]"
          >
            Make a Payment
            <ArrowUpRight size={18} />
          </a>
          <Link
            to="/customer-care"
            data-testid="hero-assistance-button"
            className="inline-flex items-center gap-2 rounded-full border border-white/30 px-8 py-4 text-base font-medium text-white transition-colors duration-300 hover:border-ember"
          >
            Get Help
          </Link>
        </motion.div>
      </div>

      <motion.div
        className="relative mt-14 lg:mt-0 w-full max-w-md mx-auto lg:max-w-none"
        initial={{ opacity: 0, scale: 0.96, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
        data-testid="hero-image"
      >
        <div className="absolute -inset-6 rounded-[2rem] bg-ember/20 blur-3xl" aria-hidden="true" />
        <img
          src="/hero-couple.jpg"
          alt="Happy Concorde Auto Acceptance customers with the keys to their financed vehicle"
          className="relative w-full aspect-[4/3] lg:aspect-[4/5] object-cover rounded-[2rem] border border-white/20 shadow-[0_40px_80px_rgba(3,8,22,0.45)]"
        />
      </motion.div>
    </div>
  </section>
);

const chapters = [
  {
    icon: CreditCard,
    title: "Payment Options",
    body: "Pay online through the customer portal, by mail, or in person. Choose the payment option that's most convenient for you.",
    to: "/make-a-payment",
    cta: "See payment options",
    testid: "chapter-payments",
  },
  {
    icon: Phone,
    title: "We're Here to Help",
    body: "Questions about your account, payment, payoff, or insurance? Our Customer Care team is ready to help.",
    to: "/customer-care",
    cta: "Contact customer service",
    testid: "chapter-assistance",
  },
  {
    icon: FileText,
    title: "Frequently Asked Questions",
    body: "Find answers about payoff quotes, title releases, insurance, payments, and more.",
    to: "/faq",
    cta: "Browse the FAQ",
    testid: "chapter-answers",
  },
];

const Manifesto = () => (
  <section className="py-24 lg:py-32" data-testid="home-manifesto">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal>
        <Overline>Customer Assistance</Overline>
        <h2 className="mt-6 font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl text-slate-900 max-w-3xl">
          Fast answers. Easy payments. <span className="text-ember">Real support.</span>
        </h2>
      </Reveal>
      <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
        {chapters.map((c, i) => (
          <Reveal key={c.title} delay={i * 0.08} className="h-full">
            <Link
              to={c.to}
              data-testid={c.testid}
              className="group h-full flex flex-col rounded-2xl border border-slate-900/10 bg-white p-7 lg:p-8 transition-all duration-300 hover:border-ember/40 hover:shadow-[0_20px_50px_rgba(10,27,61,0.08)]"
            >
              <div className="w-12 h-12 rounded-full bg-ember/10 flex items-center justify-center">
                <c.icon size={22} className="text-ember" />
              </div>
              <h3 className="mt-8 font-display font-bold text-2xl text-slate-900 tracking-tight">
                {c.title}
              </h3>
              <p className="mt-4 text-slate-600 leading-relaxed flex-1">{c.body}</p>
              <span className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-ember">
                {c.cta}
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </span>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Home = () => (
  <>
    <Hero />
    <Manifesto />
  </>
);

export default Home;
